import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type AccountOnboardingResult, type ApiError, type OnboardingTemplateResult } from '@whinerp/client';
import { formatDate } from '@whinerp/contracts/format';
import { Button, Panel, SectionLabel } from '@whinerp/ui';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';
import { useEntitlement, useSession } from '../../app/session.js';

/** Onboarding (G-199): the customer's checklist, and the template it comes from. */

const INPUT = 'h-input rounded-control border border-control bg-surface px-2 text-body text-ink';

export function AccountOnboardingPanel({ accountId }: { accountId: string }): JSX.Element | null {
  const t = useT();
  const format = useFormat();
  const queryClient = useQueryClient();
  const enabled = useEntitlement('F-080').enabled;
  const key = ['account-onboarding', accountId];
  const plan = useQuery<AccountOnboardingResult, ApiError>({ queryKey: key, queryFn: () => api.accountOnboarding({ path: { accountId } }), enabled });
  const tick = useMutation({
    mutationFn: (input: { stepId: string; done: boolean }) => api.setOnboardingStepDone({ body: input }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: key }),
  });
  const start = useMutation({ mutationFn: () => api.startAccountOnboarding({ path: { accountId }, body: {} }), onSuccess: (result) => queryClient.setQueryData(key, { plan: result.plan }) });
  if (!enabled || !plan.data) return null;
  const view = plan.data.plan;
  return (
    <Panel data-region="onboarding">
      <div className="flex flex-col gap-2">
        <SectionLabel>{t('custOnboarding.title')}</SectionLabel>
        {!view ? (
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-meta text-ink-meta">{t('custOnboarding.none')}</p>
            <Button size="small" data-onboarding-start onClick={() => start.mutate()}>{t('custOnboarding.start')}</Button>
            {start.data?.reason ? <span className="text-meta text-ink-meta">{start.data.reason}</span> : null}
          </div>
        ) : (
          <>
            <p className="text-meta text-ink-meta" data-onboarding-progress>
              {view.completedAt
                ? t('custOnboarding.complete')
                : t('custOnboarding.progress', { values: { done: String(view.steps.filter((step) => step.doneAt).length), total: String(view.steps.length) } })}
            </p>
            <ul className="flex flex-col gap-1 text-dense">
              {view.steps.map((step) => (
                <li key={step.stepId} data-onboarding-step={step.stepId} data-overdue={step.overdue} className="flex flex-wrap items-center gap-2">
                  <input type="checkbox" data-onboarding-tick checked={step.doneAt !== null} aria-label={step.title}
                    onChange={(event) => tick.mutate({ stepId: step.stepId, done: event.target.checked })} />
                  <span className={step.doneAt ? 'text-ink-meta line-through' : 'text-ink'}>{step.title}</span>
                  <span className={`ms-auto text-meta ${step.overdue ? 'text-danger-text' : 'text-ink-meta'}`}>
                    {step.doneAt ? t('custOnboarding.doneBy', { values: { name: step.doneByName ?? '' } }) : t(step.overdue ? 'custOnboarding.overdue' : 'custOnboarding.due', { values: { date: formatDate(step.dueOn, format) } })}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </Panel>
  );
}

export function OnboardingTemplatePanel(): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const queryClient = useQueryClient();
  const { can } = useSession();
  const enabled = useEntitlement('F-080').enabled && can('setup.schema.manage');
  const template = useQuery<OnboardingTemplateResult, ApiError>({ queryKey: ['onboarding-template'], queryFn: () => api.onboardingTemplate({}), enabled });
  const [steps, setSteps] = useState<Array<{ title: string; dueDays: number }>>([]);
  useEffect(() => {
    if (template.data) setSteps(template.data.steps);
  }, [template.data]);
  const save = useMutation({
    mutationFn: () => api.saveOnboardingTemplate({ body: { steps: steps.filter((step) => step.title.trim()) } }),
    onSuccess: (result) => queryClient.setQueryData(['onboarding-template'], result),
  });
  if (!enabled || !template.data) return null;
  return (
    <Panel data-region="onboarding-template">
      <div className="flex flex-col gap-2">
        <SectionLabel>{t('custOnboarding.template')}</SectionLabel>
        <p className="text-meta text-ink-meta">{t('custOnboarding.templateHelp')}</p>
        {steps.map((step, index) => (
          <div key={index} className="flex flex-wrap items-center gap-2" data-template-step={index}>
            <input className={`${INPUT} flex-1`} data-template-title aria-label={t('custOnboarding.stepName')} value={step.title}
              onChange={(event) => setSteps(steps.map((entry, at) => (at === index ? { ...entry, title: event.target.value } : entry)))} />
            <input className={`${INPUT} w-20`} type="number" min={0} max={365} data-template-days aria-label={t('custOnboarding.days')} value={step.dueDays}
              onChange={(event) => setSteps(steps.map((entry, at) => (at === index ? { ...entry, dueDays: Number(event.target.value) } : entry)))} />
            <span className="text-meta text-ink-meta">{t('custOnboarding.daysAfter')}</span>
          </div>
        ))}
        <div className="flex gap-2">
          <Button size="small" data-template-add onClick={() => setSteps([...steps, { title: '', dueDays: 7 }])}>{t('custOnboarding.addStep')}</Button>
          <Button size="small" intent="primary" data-template-save disabled={save.isPending} onClick={() => save.mutate()}>{t('custOnboarding.save')}</Button>
        </div>
        {save.error ? <p className="text-dense text-danger-text">{errorText(save.error as ApiError)}</p> : null}
      </div>
    </Panel>
  );
}
